import Guid from "_shared/guids/guid";
import {
  useRemoveAssistantAttachmentMutation,
  useUploadAssistantAttachmentMutation,
} from "ai-assistant/_api/ai-assistant-api-service";
import AssistantAttachment from "ai-assistant/attachments/assistant-attachment";
import { attachmentUploadIssue } from "ai-assistant/attachments/assistant-attachment-failure";
import {
  attachmentFileIssue,
  MAXIMUM_ASSISTANT_ATTACHMENTS_PER_MESSAGE,
  tooManyAttachmentsIssue,
  withAttachmentMediaType,
} from "ai-assistant/attachments/assistant-attachment-policy";
import React from "react";

export type ComposerAttachmentStatus = "uploading" | "uploaded" | "failed";

/** One file in the composer, from the moment it is chosen until the message carrying it is sent. */
export interface ComposerAttachment {
  /** Names the row; a file has no id until the server answers its upload. */
  key: string;
  file: File;
  status: ComposerAttachmentStatus;
  /** The server's record, once the upload has answered. */
  attachment?: AssistantAttachment;
  /** Why the upload failed, in the server's words where it gave them. */
  issue?: string;
}

export const ATTACHMENTS_UPLOADING_REASON = "Wait for your files to finish uploading.";
export const ATTACHMENTS_FAILED_REASON = "Retry or remove the files that did not upload.";

type UploadTrigger = ReturnType<typeof useUploadAssistantAttachmentMutation>[0];
type UploadOutcome = { attachment: AssistantAttachment } | { issue: string };

/**
 * Uploads one file and says how that went.
 *
 * At module level because a `try` holding conditionals makes the React compiler skip the whole
 * hook.
 */
async function uploadFile(
  upload: UploadTrigger,
  file: File,
  started: (abort: () => void) => void,
): Promise<UploadOutcome> {
  const request = upload({ file: withAttachmentMediaType(file) });
  started(() => request.abort());
  try {
    return { attachment: await request.unwrap() };
  } catch (error) {
    return { issue: attachmentUploadIssue(error) };
  }
}

/**
 * The files the user is attaching to their next assistant message (#6504).
 *
 * Each file uploads as soon as it is chosen, so the message itself stays JSON and names them by
 * id. A file the browser can already tell will be refused - its type, its size, one too many - is
 * not uploaded at all, and `notice` says why. The server checks again: it reads the content, which
 * the browser cannot.
 */
export function useAssistantAttachments() {
  const [upload] = useUploadAssistantAttachmentMutation();
  const [removeAttachment] = useRemoveAssistantAttachmentMutation();
  const [items, setItems] = React.useState<ComposerAttachment[]>([]);
  const [notice, setNotice] = React.useState<string | undefined>();
  /**
   * How to stop each upload still in flight, and which rows were removed during theirs.
   *
   * Refs rather than state: nothing renders from them. A row removed mid-upload aborts its request;
   * if the server answered first, the file it kept is taken back as soon as the answer lands.
   */
  const abortsRef = React.useRef<Map<string, () => void>>(new Map());
  const removedRef = React.useRef<Set<string>>(new Set());

  const isUploading = items.some((item) => item.status === "uploading");
  const hasFailures = items.some((item) => item.status === "failed");
  const uploaded = items
    .map((item) => item.attachment)
    .filter((attachment): attachment is AssistantAttachment => attachment !== undefined);
  const sendBlockedReason = isUploading
    ? ATTACHMENTS_UPLOADING_REASON
    : hasFailures ? ATTACHMENTS_FAILED_REASON : undefined;
  const isFull = items.length >= MAXIMUM_ASSISTANT_ATTACHMENTS_PER_MESSAGE;

  function settle(key: string, change: Partial<ComposerAttachment>) {
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...change } : item)));
  }

  async function startUpload(key: string, file: File) {
    const outcome = await uploadFile(upload, file, (abort) => abortsRef.current.set(key, abort));
    abortsRef.current.delete(key);

    if (removedRef.current.has(key)) {
      removedRef.current.delete(key);
      // Removed while the server was answering: it kept the file, so take it back.
      if ("attachment" in outcome) void removeAttachment({ attachmentId: outcome.attachment.id.value });
      return;
    }

    if ("issue" in outcome) {
      settle(key, { status: "failed", issue: outcome.issue, attachment: undefined });
      return;
    }
    settle(key, { status: "uploaded", attachment: outcome.attachment, issue: undefined });
  }

  /** Adds the files the user chose, uploading each one the browser has no reason to refuse. */
  function add(files: readonly File[]) {
    const refusals: string[] = [];
    const accepted: File[] = [];
    for (const file of files) {
      const issue = attachmentFileIssue(file);
      if (issue) refusals.push(`${file.name}: ${issue}`);
      else accepted.push(file);
    }

    const room = Math.max(0, MAXIMUM_ASSISTANT_ATTACHMENTS_PER_MESSAGE - items.length);
    if (accepted.length > room) refusals.push(tooManyAttachmentsIssue(accepted.length - room));

    const added: ComposerAttachment[] = accepted.slice(0, room).map((file) => ({
      key: Guid.generate().value,
      file,
      status: "uploading",
    }));
    setNotice(refusals.length > 0 ? refusals.join(" ") : undefined);
    if (added.length === 0) return;

    setItems((current) => [...current, ...added]);
    for (const item of added) void startUpload(item.key, item.file);
  }

  function retry(key: string) {
    const item = items.find((candidate) => candidate.key === key);
    if (item?.status !== "failed") return;

    settle(key, { status: "uploading", issue: undefined });
    void startUpload(key, item.file);
  }

  /**
   * Takes a file out of the message.
   *
   * The row goes at once. An uploaded file is deleted on the server too; if that request fails the
   * file stays there unsent, which nothing reads and the server's sweep clears, so the user is not
   * held up over it.
   */
  function remove(key: string) {
    const item = items.find((candidate) => candidate.key === key);
    if (!item) return;

    setItems((current) => current.filter((candidate) => candidate.key !== key));
    setNotice(undefined);
    if (item.status === "uploading") {
      removedRef.current.add(key);
      abortsRef.current.get(key)?.();
      return;
    }
    if (item.attachment) void removeAttachment({ attachmentId: item.attachment.id.value });
  }

  /**
   * Marks the files a refused turn carried as not sendable, with why.
   *
   * Each loses its server record, so Retry uploads it again under a new one, and Send is held
   * until every one is retried or removed. Without this the strip went on saying Attached and
   * every resend was refused the same way (#6504 review).
   */
  function reject(attachmentIds: readonly string[], issue: string) {
    const refused = new Set(attachmentIds);
    setItems((current) => current.map((item) =>
      item.attachment && refused.has(item.attachment.id.value)
        ? { ...item, status: "failed", attachment: undefined, issue }
        : item));
  }

  /** Empties the strip once the message carrying its files has been sent. */
  function clear() {
    setItems([]);
    setNotice(undefined);
  }

  return {
    items,
    notice,
    /** The uploaded files, in the order they were chosen: what the next message carries. */
    uploaded,
    /** Why the message cannot be sent yet, or nothing when the files do not stand in its way. */
    sendBlockedReason,
    isFull,
    add,
    retry,
    remove,
    reject,
    clear,
  };
}

export type AssistantAttachments = ReturnType<typeof useAssistantAttachments>;
